import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { Moon, Sun } from "lucide-react";
import { useFetch } from "../../hooks/useFetch";
import { getOwner } from "../../services/firestore";
import { userIsAdmin } from "../../services/auth";
import { auth } from "../../config/firebase";
import { useActiveSection } from "../../hooks/useActiveSection";
import { useTheme } from "../../hooks/useTheme";
import "./Navbar.css";

const NAV_ITEMS = [
  { type: "anchor", id: "skills",       label: "skills" },
  { type: "anchor", id: "projects",     label: "projects" },
  { type: "anchor", id: "hackathons",   label: "hackathons" },
  { type: "anchor", id: "achievements", label: "achievements" },
  { type: "anchor", id: "certificates", label: "certificates" },
  { type: "route",  to: "/timeline",    label: "timeline" },
  { type: "route",  to: "/blog",        label: "blog" },
  { type: "anchor", id: "contact",      label: "contact" },
];

const SECTION_IDS = NAV_ITEMS.filter((i) => i.type === "anchor").map((i) => i.id);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen]         = useState(false);
  const [isAdmin, setIsAdmin]   = useState(false);
  const location                = useLocation();
  const navigate                = useNavigate();
  const { data: owner }         = useFetch(getOwner, []);
  const activeSection           = useActiveSection(SECTION_IDS);
  const { theme, toggle: toggleTheme } = useTheme();
  const [cachedName]            = useState(() => {
    try { return localStorage.getItem("portfolio-owner-name") || ""; }
    catch { return ""; }
  });

  useEffect(() => {
    if (!owner?.name) return;
    try { localStorage.setItem("portfolio-owner-name", owner.name); } catch { /* storage unavailable */ }
  }, [owner]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsAdmin(await userIsAdmin(user));
    });

    return unsubscribe;
  }, []);

  const handleAnchorClick = (e, id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      e.preventDefault();
      navigate(`/#${id}`);
    }
  };

  const handleLogoClick = (e) => {
    setOpen(false);
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (location.hash) {
        window.history.replaceState(null, "", "/");
      }
    }
  };

  const firstName = (owner?.name || cachedName || "rajat").split(" ")[0]?.toLowerCase() || "rajat";
  const onHome = location.pathname === "/";

  return (
    <nav className={`navbar ${scrolled ? "navbar--scrolled" : ""}`}>
      <div className="navbar__inner container">
        <Link
          className="navbar__logo"
          to="/"
          aria-label="home"
          onClick={handleLogoClick}
        >
          {firstName}
          <span className="navbar__logo-dot">.</span>
        </Link>

        <ul className={`navbar__links ${open ? "navbar__links--open" : ""}`}>
          {NAV_ITEMS.map((item) => {
            const isActive = item.type === "anchor"
              ? (onHome && activeSection === item.id)
              : (location.pathname === item.to);

            const cls = `navbar__link ${isActive ? "navbar__link--active" : ""}`.trim();

            return (
              <li key={item.label}>
                {item.type === "anchor" ? (
                  <a
                    href={`#${item.id}`}
                    className={cls}
                    aria-current={isActive ? "page" : undefined}
                    onClick={(e) => handleAnchorClick(e, item.id)}
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link to={item.to} className={cls} aria-current={isActive ? "page" : undefined}>
                    {item.label}
                  </Link>
                )}
              </li>
            );
          })}
          {isAdmin && (
            <li>
              <Link to="/admin" className="navbar__link">admin</Link>
          </li>
        )}
        </ul>

        <div className="navbar__right">
          <button
            className="navbar__theme-toggle"
            type="button"
            onClick={() => {
              setOpen(false);
              toggleTheme();
            }}
            aria-label={`switch to ${theme === "dark" ? "light" : "dark"} mode`}
            title="toggle theme"
          >
            {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
          </button>

          <button
            className={`navbar__hamburger ${open ? "navbar__hamburger--open" : ""}`}
            onClick={() => setOpen(!open)}
            aria-label="menu"
            aria-expanded={open}
          >
            <span /><span /><span />
          </button>
        </div>
      </div>
    </nav>
  );
}
